/**
 * MaternityTracker.jsx
 * 
 * Main list view for maternity leave records.
 * Styled to match existing dashboard patterns.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../axiosConfig';
import {
    Box,
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Paper,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert
} from '@mui/material';
import {
    ChildCare as MaternityIcon,
    Add as AddIcon,
    Warning as WarningIcon,
    EventAvailable as ReturnIcon,
    Schedule as PendingIcon,
    ArrowBack as BackIcon,
    Visibility as ViewIcon,
    Home as HomeIcon
} from '@mui/icons-material';

const MaternityTracker = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [records, setRecords] = useState([]);
    const [dashboard, setDashboard] = useState({
        active_leaves: 0,
        overdue_actions: 0,
        returning_soon: 0,
        pending_reminders: 0
    });
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [recordsRes, dashboardRes] = await Promise.all([
                api.get('/maternity'),
                api.get('/maternity/dashboard')
            ]);
            setRecords(recordsRes.data.records || []);
            setDashboard(dashboardRes.data || {});
        } catch (err) {
            console.error('Failed to fetch maternity data', err);
            setError('Failed to load maternity records');
        } finally {
            setLoading(false);
        }
    };

    const quickStats = [
        {
            title: 'Active Leaves',
            value: dashboard.active_leaves || 0,
            icon: <MaternityIcon />,
            color: '#8b5cf6',
            subtitle: 'Currently on leave'
        },
        {
            title: 'Actions Required',
            value: dashboard.overdue_actions || 0,
            icon: <WarningIcon />,
            color: dashboard.overdue_actions > 0 ? '#f59e0b' : '#22c55e',
            subtitle: 'Overdue items'
        },
        {
            title: 'Returning Soon',
            value: dashboard.returning_soon || 0,
            icon: <ReturnIcon />,
            color: '#3b82f6',
            subtitle: 'Within 4 weeks'
        },
        {
            title: 'Pending Reminders',
            value: dashboard.pending_reminders || 0,
            icon: <PendingIcon />,
            color: '#64748b',
            subtitle: 'To action'
        }
    ];

    const getStatusChip = (status) => {
        const statusConfig = {
            notified: { label: 'Notified', color: 'info' },
            on_leave: { label: 'On Leave', color: 'success' },
            returned: { label: 'Returned', color: 'default' },
            resigned: { label: 'Resigned', color: 'error' },
            extended: { label: 'Extended', color: 'warning' },
            cancelled: { label: 'Cancelled', color: 'default' }
        };
        const config = statusConfig[status] || { label: status, color: 'default' };
        return <Chip label={config.label} color={config.color} size="small" />;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '�';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                    color: 'white',
                    p: 4,
                    mb: 4,
                    borderRadius: 0
                }}
            >
                <Container maxWidth="lg">
                    <Button
                        startIcon={<HomeIcon />}
                        onClick={() => navigate('/team')}
                        sx={{ color: 'white', mb: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                    >
                        Back to Team
                    </Button>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                                Maternity Tracker
                            </Typography>
                            <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                Track maternity leave, statutory dates, and compliance
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/team/maternity/new')}
                            sx={{
                                bgcolor: 'white',
                                color: '#7c3aed',
                                fontWeight: 600,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                            }}
                        >
                            New Record
                        </Button>
                    </Box>
                </Container>
            </Paper>

            <Container maxWidth="lg">
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
                )}

                {/* Quick Stats */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {quickStats.map((stat, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Card
                                elevation={0}
                                sx={{
                                    border: '1px solid #e5e7eb',
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
                                    }
                                }}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                        <Box sx={{ 
                                            width: 48, 
                                            height: 48, 
                                            borderRadius: 2, 
                                            bgcolor: `${stat.color}20`, 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            color: stat.color
                                        }}>
                                            {stat.icon}
                                        </Box>
                                        {stat.title === 'Actions Required' && dashboard.overdue_actions > 0 && (
                                            <Chip
                                                label="Action needed"
                                                size="small"
                                                color="warning"
                                                sx={{ height: 20, fontSize: 10 }}
                                            />
                                        )}
                                    </Box>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: stat.color, mb: 0.5 }}>
                                        {stat.value}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                        {stat.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {stat.subtitle}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Info Card */}
                <Card elevation={0} sx={{ border: '1px solid #e5e7eb', mb: 4, bgcolor: '#f5f3ff' }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#7c3aed', mb: 1 }}>
                            Key Statutory Deadlines
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>28 days</strong> � Employer must respond to maternity notification
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>8 weeks</strong> � Notice required to change return date
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>10 days</strong> � Maximum KIT days during leave
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Records Table */}
                <Card elevation={0} sx={{ border: '1px solid #e5e7eb' }}>
                    <CardContent sx={{ p: 0 }}>
                        <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                All Maternity Records
                            </Typography>
                        </Box>
                        
                        {records.length === 0 ? (
                            <Box sx={{ p: 6, textAlign: 'center' }}>
                                <Box
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: 3,
                                        bgcolor: '#f5f3ff',
                                        color: '#8b5cf6',
                                        mx: 'auto',
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <MaternityIcon sx={{ fontSize: 40 }} />
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    No maternity records yet
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Create a record when an employee notifies you of their pregnancy
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => navigate('/team/maternity/new')}
                                    sx={{
                                        background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                        '&:hover': { opacity: 0.9 }
                                    }}
                                >
                                    Create First Record
                                </Button>
                            </Box>
                        ) : (
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f9fafb' }}>
                                            <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>EWC</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Leave Dates</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>KIT Days</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Next Action</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {records.map((record) => (
                                            <TableRow
                                                key={record.id}
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => navigate(`/team/maternity/${record.id}`)}
                                            >
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Box sx={{ 
                                                            width: 36, 
                                                            height: 36, 
                                                            borderRadius: 1.5, 
                                                            bgcolor: '#f5f3ff', 
                                                            color: '#8b5cf6',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontWeight: 600,
                                                            fontSize: 14
                                                        }}>
                                                            {record.employee_name?.charAt(0) || 'E'}
                                                        </Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {record.employee_name || 'Unknown'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{getStatusChip(record.status)}</TableCell>
                                                <TableCell>{formatDate(record.ewc)}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatDate(record.intended_start_date)} � {formatDate(record.expected_return_date)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={`${record.kit_days_used || 0}/10`}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontWeight: 500 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {record.next_reminder ? (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {record.next_reminder?.date ? `${record.next_reminder.date} — ${record.next_reminder.type_label}` : record.next_reminder}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">�</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        startIcon={<ViewIcon />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/team/maternity/${record.id}`);
                                                        }}
                                                        sx={{ color: '#8b5cf6' }}
                                                    >
                                                        View
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default MaternityTracker;